import { useBankAccount } from "@/hooks/firestore/user/useBankAccounts";
import type { WithdrawCurrencyId } from "../types";
import type { WithdrawStepName } from "../utils/registry";
import { useBlockingCardVerification } from "./useBlockingCardVerification";

export const useAudWithdrawEntryStep = (): {
  step: WithdrawStepName;
  isLoading: boolean;
} => {
  const { bankAccount, isLoading } = useBankAccount();

  return {
    step: bankAccount ? "withdraw" : "withdraw/bank-details",
    isLoading,
  };
};

/**
 * `Wizard.Root` reads `initialStep` once on mount, so the gates that decide
 * where an AUD user lands have to be resolved before the wizard renders.
 * A card requirement that lands after that is caught by the wizard's own
 * gate instead.
 *
 * The reads run for every currency because hooks can't be called
 * conditionally; for NZD and CAD accounts they simply return nothing.
 */
export const useWithdrawInitialStep = (
  currency: WithdrawCurrencyId,
): { initialStep: WithdrawStepName; isSettled: boolean } => {
  const { step: entryStep, isLoading: entryStepLoading } =
    useAudWithdrawEntryStep();
  const { verification, isLoading: verificationsLoading } =
    useBlockingCardVerification();

  if (currency !== "AUD") {
    return { initialStep: "withdraw", isSettled: true };
  }

  if (entryStepLoading || verificationsLoading) {
    return { initialStep: "withdraw", isSettled: false };
  }

  if (verification) {
    return { initialStep: "withdraw/card-verification", isSettled: true };
  }

  return { initialStep: entryStep, isSettled: true };
};
